// src/components/kit/link-in-bio/LinkInBioRenderer.tsx

import { useState } from 'react';
import type { PublicPageData, LinkInBioFormState, LinkItem, SmartMusicLink } from '@/types/linkInBio';
import { STYLES, PALETTES } from './themes';

interface LinkInBioRendererProps {
  data: PublicPageData | LinkInBioFormState;
  onLinkClick?: (linkId: string, label: string) => void;
  onEmailSubmit?: (email: string) => Promise<void>;
  isPreview?: boolean;
}

/** Platform display names and icons for the smart music link modal. */
const PLATFORM_LABELS: Record<string, string> = {
  spotify: 'Spotify',
  apple_music: 'Apple Music',
  youtube_music: 'YouTube Music',
  tidal: 'Tidal',
  deezer: 'Deezer',
  soundcloud: 'SoundCloud',
  amazon_music: 'Amazon Music',
};

/**
 * Social platform detection — maps URL patterns to platform keys.
 * Only the 5 platforms we show icons for.
 */
type SocialPlatform = 'instagram' | 'facebook' | 'tiktok' | 'youtube' | 'bandcamp';

const SOCIAL_PLATFORM_PATTERNS: { platform: SocialPlatform; patterns: RegExp[] }[] = [
  {
    platform: 'instagram',
    patterns: [/instagram\.com/i, /instagr\.am/i],
  },
  {
    platform: 'facebook',
    patterns: [/facebook\.com/i, /fb\.com/i, /fb\.me/i],
  },
  {
    platform: 'tiktok',
    patterns: [/tiktok\.com/i],
  },
  {
    platform: 'youtube',
    patterns: [/youtube\.com/i, /youtu\.be/i],
  },
  {
    platform: 'bandcamp',
    patterns: [/bandcamp\.com/i],
  },
];

function detectSocialPlatform(url: string): SocialPlatform | null {
  for (const { platform, patterns } of SOCIAL_PLATFORM_PATTERNS) {
    for (const pattern of patterns) {
      if (pattern.test(url)) {
        return platform;
      }
    }
  }
  return null;
}

/** SVG icons for social platforms. */
const SOCIAL_ICONS: Record<SocialPlatform, React.ReactNode> = {
  instagram: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  ),
  facebook: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  ),
  tiktok: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  ),
  youtube: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  ),
  bandcamp: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm3.5 15.09H5.472l3.028-6.18h10.028l-3.028 6.18z" />
    </svg>
  ),
};

export default function LinkInBioRenderer({
  data,
  onLinkClick,
  onEmailSubmit,
  isPreview = false,
}: LinkInBioRendererProps) {
  const [emailValue, setEmailValue] = useState('');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [emailError, setEmailError] = useState('');
  const [showPlatforms, setShowPlatforms] = useState(false);

  // Resolve theme configs
  const styleKey = data.style || 'modern';
  const paletteKey = data.palette || 'dark-gold';
  const backgroundKey = data.background || 'solid';

  const style = STYLES[styleKey] || STYLES.modern;
  const palette = PALETTES[paletteKey] || PALETTES['dark-gold'];

  // Background
  const parseBgStyle = (cssText: string): React.CSSProperties => {
    const match = cssText.match(/background:\s*(.+)/);
    if (match) {
      return { background: match[1] };
    }
    return {};
  };

  let bgClass = palette.bg;
  let bgStyle: React.CSSProperties = {};

  if (backgroundKey === 'gradient') {
    bgClass = '';
    bgStyle = parseBgStyle(palette.gradientBg);
  } else if (palette.solidBg) {
    bgClass = '';
    bgStyle = parseBgStyle(palette.solidBg);
  }

  // Link button classes
  const linkBg = palette.isLight ? (palette.linkBg || 'bg-white') : palette.cardBg;
  const linkText = palette.isLight ? (palette.cardText || palette.text) : palette.text;
  const linkBorder = palette.cardBorder;
  const linkHover = palette.cardHover;
  const isGlass = palette.isGlass;
  const accentBorder = palette.accentBorder || '';

  // Extract data fields
  const displayName = data.display_name || '';
  const tagline = data.tagline || '';
  const avatarUrl = data.avatar_url || null;
  const avatarPosition = ('avatar_position' in data ? data.avatar_position : undefined) || 'center';

  // Handle both PublicPageData and FormState shapes
  const smartMusicLink: SmartMusicLink | null = data.smart_music_link || null;
  const links: LinkItem[] = data.links || [];
  const emailCapture = data.email_capture || { enabled: false, prompt_text: '', button_label: '' };

  // Campaign banner
  let campaignBanner: { enabled: boolean; text: string } | null = null;
  if ('campaign_banner' in data && data.campaign_banner) {
    campaignBanner = data.campaign_banner;
  } else if ('campaign_banner_enabled' in data && data.campaign_banner_enabled) {
    campaignBanner = {
      enabled: true,
      text: (data as LinkInBioFormState).campaign_banner_text || '',
    };
  }

  // Detect social links from the links list
  const socialLinks: { platform: SocialPlatform; link: LinkItem }[] = [];
  links.forEach((link) => {
    const platform = detectSocialPlatform(link.url);
    if (platform && !socialLinks.some((s) => s.platform === platform)) {
      socialLinks.push({ platform, link });
    }
  });

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPreview || !onEmailSubmit) return;

    const trimmed = emailValue.trim();
    if (!trimmed) return;

    setEmailStatus('loading');
    setEmailError('');

    try {
      await onEmailSubmit(trimmed);
      setEmailStatus('success');
      setEmailValue('');
    } catch (err: any) {
      setEmailStatus('error');
      setEmailError(err?.error?.message || 'Something went wrong');
    }
  };

  const handleLinkClick = (linkId: string, label: string, url: string) => {
    if (isPreview) return;
    onLinkClick?.(linkId, label);
    if (!isPreview) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handlePlatformClick = (platform: string, url: string) => {
    if (isPreview) return;
    onLinkClick?.(`smart_music_${platform}`, `Listen - ${PLATFORM_LABELS[platform] || platform}`);
    setShowPlatforms(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSocialClick = (link: LinkItem) => {
    if (isPreview) return;
    onLinkClick?.(link.link_id, link.label);
    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  // Use min-h-screen for public pages so background fills the viewport,
  // but min-h-full for the builder preview so it fills the preview frame.
  const heightClass = isPreview ? 'min-h-full' : 'min-h-screen';

  return (
    <div
      className={`${heightClass} px-4 py-12 sm:px-6 ${bgClass}`}
      style={bgStyle}
    >
      <div className="mx-auto max-w-lg">
        {/* Avatar */}
        <div className="mb-5 flex justify-center">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={displayName}
              className={`h-24 w-24 rounded-full border-2 object-cover ${palette.avatarBorder}`}
              style={{ objectPosition: avatarPosition }}
            />
          ) : (
            <div
              className={`flex h-24 w-24 items-center justify-center rounded-full border-2 ${palette.avatarBorder} ${palette.cardBg}`}
            >
              <svg className={`h-10 w-10 ${palette.subtext}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          )}
        </div>

        {/* Display Name */}
        {displayName && (
          <h1 className={`mb-1 text-center text-xl font-bold ${palette.text} ${style.headingFont} break-words`}>
            {displayName}
          </h1>
        )}

        {/* Tagline */}
        {tagline && (
          <p className={`mb-4 text-center text-sm ${palette.subtext} ${style.bodyFont} break-words`}>
            {tagline}
          </p>
        )}

        {/* Social Icons */}
        {socialLinks.length > 0 && (
          <div className="mb-8 flex flex-wrap items-center justify-center gap-4">
            {socialLinks.map(({ platform, link }) => (
              <button
                key={platform}
                onClick={() => handleSocialClick(link)}
                className={`${palette.socialColor} transition-opacity hover:opacity-75`}
                title={link.label || platform.charAt(0).toUpperCase() + platform.slice(1)}
              >
                {SOCIAL_ICONS[platform]}
              </button>
            ))}
          </div>
        )}

        {/* Campaign Banner */}
        {campaignBanner?.enabled && campaignBanner.text && (
          <div className={`${palette.bannerBg} border ${palette.bannerBorder} ${style.buttonRadius} mb-6 px-4 py-3`}>
            <p className={`${palette.bannerText} text-center text-sm font-medium ${style.bodyFont} break-words`}>
              🔥 {campaignBanner.text}
            </p>
          </div>
        )}

        {/* Smart Music Link */}
        {smartMusicLink && smartMusicLink.platforms && Object.keys(smartMusicLink.platforms).length > 0 && (
          <div className="relative mb-4">
            <button
              onClick={() => !isPreview && setShowPlatforms(!showPlatforms)}
              className={`flex w-full items-center justify-center gap-2 ${palette.accent} ${palette.accentHover} ${palette.accentText} ${style.buttonRadius} px-6 py-4 font-semibold transition-colors ${accentBorder} ${style.bodyFont}`}
            >
              <svg className="h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
              </svg>
              <span className="break-words">{smartMusicLink.label || 'Listen Now'}</span>
            </button>

            {/* Platform dropdown */}
            {showPlatforms && (
              <div className={`absolute left-0 right-0 top-full z-10 mt-2 ${isGlass ? 'glass-card' : `${palette.cardBg} border ${palette.cardBorder}`} ${style.buttonRadius} overflow-hidden p-2 shadow-xl`}>
                {Object.entries(smartMusicLink.platforms).map(([platform, url]) => (
                  <button
                    key={platform}
                    onClick={() => handlePlatformClick(platform, url)}
                    className={`flex w-full items-center gap-3 ${style.buttonRadius} px-4 py-3 text-left text-sm ${palette.text} ${palette.cardHover} transition-colors ${style.bodyFont}`}
                  >
                    <span className="font-medium">{PLATFORM_LABELS[platform] || platform}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Custom Links */}
        {links.length > 0 && (
          <div className="mb-8 space-y-3">
            {links.map((link) => (
              <button
                key={link.link_id}
                onClick={() => handleLinkClick(link.link_id, link.label, link.url)}
                className={`block w-full ${
                  isGlass
                    ? 'glass-button'
                    : `${linkBg} ${linkHover} border ${linkBorder}`
                } ${linkText} ${style.buttonRadius} px-6 py-3.5 text-center font-medium transition-colors ${style.bodyFont} break-words`}
              >
                {link.label}
              </button>
            ))}
          </div>
        )}

        {/* Email Capture */}
        {emailCapture.enabled && (
          <div className={`${isGlass ? 'glass-card' : `${palette.cardBg} border ${palette.cardBorder}`} ${style.emailRadius} mb-8 overflow-hidden p-4 sm:p-5`}>
            <p className={`${palette.text} mb-3 text-center text-sm font-medium ${style.bodyFont} break-words`}>
              {emailCapture.prompt_text || 'Join my mailing list'}
            </p>

            {emailStatus === 'success' ? (
              <p className={`text-center text-sm font-medium ${palette.accentColor}`}>
                ✓ You're subscribed!
              </p>
            ) : (
              <form onSubmit={handleEmailSubmit} className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  disabled={isPreview}
                  className={`min-w-0 flex-1 ${palette.inputBg} border ${palette.inputBorder} ${style.buttonRadius} px-4 py-2.5 text-sm ${palette.text} ${palette.placeholder} focus:outline-none ${palette.inputFocus}`}
                />
                <button
                  type="submit"
                  disabled={isPreview || emailStatus === 'loading'}
                  className={`${palette.accent} ${palette.accentHover} ${palette.accentText} ${style.buttonRadius} px-4 py-2.5 text-sm font-medium transition-colors ${accentBorder} ${style.bodyFont}`}
                >
                  {emailStatus === 'loading' ? '...' : emailCapture.button_label || 'Subscribe'}
                </button>
              </form>
            )}

            {emailStatus === 'error' && emailError && (
              <p className="mt-2 text-center text-xs text-red-400">{emailError}</p>
            )}
          </div>
        )}

        {/* Footer */}
        <div className={`border-t pt-4 text-center ${palette.footerBorder}`}>
          <p className={`text-xs ${palette.footerText}`}>
            Powered by{' '}
            <a
              href="https://www.bandjerry.com"
              className={`${palette.accentLight} font-medium hover:underline`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => isPreview && e.preventDefault()}
            >
              BandJerry
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
